/**
 * Braze readiness gate.
 *
 * WHY THIS EXISTS
 *
 * The Braze web SDK discards anything logged before `initialize()` has run.
 * From the SDK source (@braze/web-sdk 5.9.1):
 *
 *   // src/Core/log-custom-event.js
 *   export function logCustomEvent(t, e) {
 *     if (!r.rr()) return !1;          // rr() -> isInitialized
 *
 *   // src/managers/braze-instance.js
 *   rr() { return !this.Za() && (!!this.nn() || (console.warn(p), !1)); }
 *   nn() { return this.isInitialized; }
 *
 * It returns `false` and logs a console warning — it does **not** queue. Our
 * adapter ignored that return value, so any Braze-routed event fired before
 * initialisation finished was lost with no error surfaced.
 *
 * That window is real: BrazeProvider calls `initBraze()`, which is a
 * fire-and-forget dynamic `import('./braze-exports')` followed by
 * `initialize()`. Anything emitted while that import is in flight vanished.
 *
 * Note the contrast with Amplitude, which DOES queue pre-init calls
 * (@amplitude/analytics-core `core-client.js` pushes to `dispatchQ` and
 * replays via `runQueuedFunctions`). Braze needs this shim precisely because
 * it does not.
 */

type QueuedCall = () => void;

/** Bounded so a Braze SDK that never loads (ad blocker, network failure)
 *  cannot grow this without limit. The init window is short; 100 is generous. */
const MAX_QUEUED = 100;

let ready = false;
let queue: QueuedCall[] = [];
let droppedWhileWaiting = 0;

/**
 * Called once `initialize()` has actually completed. Flushes anything that
 * arrived first, in the order it was emitted.
 */
export const markBrazeReady = (): void => {
  if (ready) return;
  ready = true;

  const pending = queue;
  queue = [];

  if (droppedWhileWaiting > 0) {
    // eslint-disable-next-line no-console
    console.warn(
      `[braze] ${droppedWhileWaiting} event(s) dropped before init (queue limit ${MAX_QUEUED})`,
    );
    droppedWhileWaiting = 0;
  }

  pending.forEach(call => {
    try {
      call();
    } catch (err) {
      // One bad event must never stop the rest of the flush.
      // eslint-disable-next-line no-console
      console.warn('[braze] queued call failed during flush', err);
    }
  });
};

/**
 * Run `call` now if Braze is initialised, otherwise hold it until it is.
 *
 * Never throws: this sits on the analytics path and must not be able to break
 * rendering.
 */
export const runWhenBrazeReady = (call: QueuedCall): void => {
  if (ready) {
    try {
      call();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[braze] call failed', err);
    }
    return;
  }

  if (queue.length >= MAX_QUEUED) {
    // Drop the oldest so the most recent activity survives.
    queue.shift();
    droppedWhileWaiting += 1;
  }
  queue.push(call);
};

export const isBrazeReady = (): boolean => ready;

/** Test-only. Restores module state between cases. */
export const __resetBrazeQueue = (): void => {
  ready = false;
  queue = [];
  droppedWhileWaiting = 0;
};
